import { useMemo } from 'react'
import {
  type EntityId,
  type PreferenceGetInput,
  type PreferenceGetSuccess,
  type StrictSubareaPreference,
  unique,
} from '@terros/common'
import { useQueries } from '@tanstack/react-query'
import { preferenceKey } from '../../keys'
import { useServerRequest } from '../../api'

type StrictSubareaState = {
  mode: StrictSubareaPreference
  isLoading: boolean
}

type StrictSubareaPreferenceResults = {
  modes: StrictSubareaPreference[]
  isLoading: boolean
}

const STRICT_SUBAREA_MODE_WEIGHT = new Map<StrictSubareaPreference, number>([
  ['off', 0],
  ['warn', 1],
  ['block', 2],
])

export function useStrictSubareaAssignment(targetIds: EntityId[] = []): StrictSubareaState {
  const { modes, isLoading } = useStrictSubareaPreferenceResults(targetIds)

  const mode: StrictSubareaPreference = useMemo(() => {
    return modes.reduce<StrictSubareaPreference>((strictest, current) => {
      const strictestWeight = STRICT_SUBAREA_MODE_WEIGHT.get(strictest) ?? 0
      const currentWeight = STRICT_SUBAREA_MODE_WEIGHT.get(current) ?? 0
      return currentWeight > strictestWeight ? current : strictest
    }, 'off')
  }, [modes])

  return {
    mode,
    isLoading,
  }
}

function useStrictSubareaPreferenceResults(targetIds: EntityId[]): StrictSubareaPreferenceResults {
  const serverRequest = useServerRequest<
    PreferenceGetInput<'strictSubareaAssignment'>,
    PreferenceGetSuccess<'strictSubareaAssignment'>
  >('/preference/get')
  const uniqueTargetIds = useMemo(() => unique(targetIds), [targetIds])
  const preferenceResults = useQueries({
    queries: uniqueTargetIds.map((targetId) => ({
      queryKey: preferenceKey('strictSubareaAssignment', targetId),
      queryFn: async () => {
        const result = await serverRequest({ preference: 'strictSubareaAssignment', targetId })
        return result.value
      },
      retry: false,
    })),
  })

  return useMemo(
    () => ({
      modes: preferenceResults.map((result) => result.data ?? 'off'),
      isLoading: preferenceResults.some((result) => result.isLoading),
    }),
    [preferenceResults]
  )
}
